import dico from "./language/dico.js";
import { PositionRack, positionsRack } from "./types.js";

/**
 * Collapses rack position free text for alias matching (case/spacing insensitive).
 *
 * @param raw - User or import string.
 * @returns Normalized uppercase token without spaces.
 */
function normalizeRackToken(raw: string): string {
  return raw.trim().toUpperCase().replace(/\s+/g, "");
}

/**
 * Returns the localized label for a stored {@link PositionRack} value.
 *
 * Storage stays FR-canonical (`EXT` / `INT` / `1/2 D` / `1/2 G`);
 * English UI shows `OUT` / `IN` / `1/2 R` / `1/2 L`.
 * Accepts FR/EN aliases so display never leaks a raw storage token in EN.
 *
 * @param value - Canonical position stored on the athlete (or unknown string).
 * @returns Label for the active app language, or the raw value if unknown.
 * @example
 * // with language "en" → "IN"
 * formatPositionRack(PositionRack.INT);
 */
export function formatPositionRack(value: string | null | undefined): string {
  if (!value) {
    return "";
  }
  const labels = dico().athletes.rackPositions;
  const token = normalizeRackToken(value);
  // Canonical + EN aliases (OUT/IN/1/2 R/1/2 L) → localized label
  if (token === "EXT" || token === "OUT") {
    return labels.ext;
  }
  if (token === "INT" || token === "IN") {
    return labels.int;
  }
  if (
    token === normalizeRackToken(PositionRack.DemiDroit) ||
    token === "1/2R" ||
    token === "1/2DROIT" ||
    token === "1/2RIGHT"
  ) {
    return labels.demiDroit;
  }
  if (
    token === normalizeRackToken(PositionRack.DemiGauche) ||
    token === "1/2L" ||
    token === "1/2GAUCHE" ||
    token === "1/2LEFT"
  ) {
    return labels.demiGauche;
  }
  return value;
}

/**
 * Formats a binding value for on-screen text when the path is a rack position.
 * Keeps canonical values in InjectedData / conditions; only the final label is localized.
 *
 * @param path - Dot path of the binding (e.g. `currentAthlete.rackPosition`).
 * @param value - Resolved raw value (may be non-string).
 * @returns Localized label when the leaf is `rackPosition` / `positionRack`, else `String(value)`.
 */
export function formatBindingPathDisplayValue(
  path: string | undefined,
  value: unknown,
): string {
  if (value == null) {
    return "";
  }
  const leaf = path
    ?.split(".")
    .filter((segment) => segment.length > 0)
    .pop();
  if (leaf === "rackPosition" || leaf === "positionRack") {
    return formatPositionRack(String(value));
  }
  return String(value);
}

/**
 * Builds select options: canonical `id` + localized `value` label.
 *
 * @returns Options suitable for DataGrid / MUI Select (id = persisted value).
 */
export function getPositionRackSelectOptions(): {
  id: string;
  value: string;
}[] {
  return positionsRack.map((position) => ({
    id: position,
    value: formatPositionRack(position),
  }));
}

/**
 * Localized list of rack position labels for the current language.
 * Used in Excel template dropdowns; pair with {@link parsePositionRack} on import.
 *
 * @returns Display labels in app language order (same order as `positionsRack`).
 */
export function getLocalizedPositionRackValues(): string[] {
  return positionsRack.map((position) => formatPositionRack(position));
}

/**
 * Maps free-form FR/EN rack text to the canonical {@link PositionRack} stored in DB.
 *
 * Accepts FR (`EXT`, `INT`, `1/2 D`, `1/2 G`) and EN (`OUT`, `IN`, `1/2 R`, `1/2 L`)
 * plus compact forms without spaces.
 *
 * @param raw - Cell value from import or user input.
 * @returns Canonical enum member, defaulting to `EXT` when empty/unknown.
 */
export function parsePositionRack(
  raw: string | null | undefined,
): PositionRack {
  if (!raw || !raw.trim()) {
    return PositionRack.EXT;
  }
  const token = normalizeRackToken(raw);
  // Exact canonical match first
  const exact = positionsRack.find((p) => normalizeRackToken(p) === token);
  if (exact) {
    return exact;
  }
  // English / alternate aliases → canonical storage keys
  if (token === "OUT") {
    return PositionRack.EXT;
  }
  if (token === "IN") {
    return PositionRack.INT;
  }
  if (token === "1/2R" || token === "1/2DROIT" || token === "1/2RIGHT") {
    return PositionRack.DemiDroit;
  }
  if (token === "1/2L" || token === "1/2GAUCHE" || token === "1/2LEFT") {
    return PositionRack.DemiGauche;
  }
  return PositionRack.EXT;
}
